import { getErrorCode } from './errorCode'

/**
 * A failed request, in Spanish, for a plant screen.
 *
 * The backend's own `message` is never rendered: 4xx messages are written in
 * English for whoever reads the log ('Station group not found', 'Manager role
 * required'), and every 5xx is replaced by the API with the fixed 'Internal
 * server error' — so there is nothing to show even if we wanted to. The code is
 * the only part of the answer that is a contract, so the copy hangs off the code.
 */
const MESSAGES: Record<string, string> = {
  BAD_REQUEST: 'Hay algún dato incorrecto. Revísalo e inténtalo de nuevo.',
  UNAUTHORIZED: 'Tu sesión ha caducado. Vuelve a entrar.',
  FORBIDDEN: 'No tienes permiso para esto.',
  NOT_FOUND: 'Esto ya no existe. Puede que haya cambiado mientras la pantalla estaba abierta.',
  CONFLICT: 'Ese valor ya está en uso.',
  PRECONDITION_FAILED: 'La operación no se puede hacer en este estado.',
  TIMEOUT: 'La operación ha tardado demasiado. Inténtalo de nuevo.',
  TOO_MANY_REQUESTS: 'Demasiados intentos. Espera unos segundos e inténtalo de nuevo.',
  INTERNAL_SERVER_ERROR: 'Error del servidor. Vuelve a intentarlo; si sigue fallando, avisa a mantenimiento.',
}

/**
 * The answer for a failure with no tRPC code at all: the network dropped, or the
 * silent refresh failed and `makeRefreshFetch` threw a bare
 * `Error('UNAUTHORIZED')`. On a plant tablet the first is the likely one.
 */
const UNKNOWN_FAILURE = 'No hemos podido conectar con el servidor. Comprueba la conexión e inténtalo de nuevo.'

/**
 * What a failed login says. The API collapses "no such user", "wrong password"
 * and "account inactive" into one `UNAUTHORIZED`/'Invalid credentials' so nobody
 * can probe which emails exist; splitting them apart here would hand that back.
 * It is also why the login screen cannot use the generic UNAUTHORIZED copy: the
 * session has not expired, it never started.
 */
const CREDENTIALS_REJECTED =
  'Email o contraseña incorrectos. Si no consigues entrar, avisa a tu responsable.'

/** The Spanish copy for any query or mutation error. */
export const trpcMessage = (error: unknown): string => MESSAGES[getErrorCode(error)] ?? UNKNOWN_FAILURE

/** The Spanish copy for a failed sign-in — see `CREDENTIALS_REJECTED`. */
export const loginErrorMessage = (error: unknown): string =>
  getErrorCode(error) === 'UNAUTHORIZED' ? CREDENTIALS_REJECTED : trpcMessage(error)

/**
 * What a failed Google sign-in says. Google vouched for the address or it did
 * not (UNAUTHORIZED); the address is not given access in WASP (FORBIDDEN —
 * accounts are created beforehand, never on a first sign-in); or this deploy
 * has no Google client at all (PRECONDITION_FAILED).
 */
const GOOGLE_MESSAGES: Record<string, string> = {
  UNAUTHORIZED: 'Google no ha podido confirmar tu cuenta. Inténtalo de nuevo.',
  FORBIDDEN: 'Tu cuenta de Google no tiene acceso. Pide a quien gestiona los usuarios que te dé de alta.',
  PRECONDITION_FAILED: 'El acceso con Google no está disponible aquí. Entra con tu email y contraseña.',
}

/** The Spanish copy for a failed Google sign-in — see `GOOGLE_MESSAGES`. */
export const googleLoginErrorMessage = (error: unknown): string =>
  GOOGLE_MESSAGES[getErrorCode(error)] ?? trpcMessage(error)
